import { axios } from "..";
import { IEmailSend } from "../interfaces/models/IEmailSend";

export type ICreateEmailSendRequest = Pick<
  IEmailSend,
  | "templateId"
  | "recipientFilter"
  | "sendType"
  | "scheduledDateTime"
  | "timezoneType"
  | "createdBy"
  | "createdByName"
  | "distributionListId"
  | "distributionListName"
  | "isTestList"
>;

export const createEmailSend = async (
  payload: ICreateEmailSendRequest,
): Promise<IEmailSend> => {
  const response = await axios.Post("./emailSend", payload);
  return response.data;
};

export type EmailSendListMode = "pendingScheduled" | "sent";

export interface IEmailSendListItem {
  id: string;
  templateId: string;
  emailNumber?: string;
  templateTitle?: string;
  sendType?: IEmailSend["sendType"];
  scheduledDateTime?: number;
  sentDateTime?: number;
  timezoneType?: IEmailSend["timezoneType"];
  timezoneLabel?: "EST" | "Regional";
  scheduledBy?: string;
  listName?: string;
  recipientCount: number;
  status: string;
  errorDetails?: string;
  failedAt?: number;
}

export interface IEmailSendListPagination {
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface IEmailSendListPageResponse {
  items: IEmailSendListItem[];
  pagination: IEmailSendListPagination;
}

export type IEmailSendListQueryOptions = {
  pageIndex?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export const getEmailSendList = async (
  mode: EmailSendListMode,
  templateId?: string,
  options?: IEmailSendListQueryOptions,
): Promise<IEmailSendListItem[]> => {
  const params = new URLSearchParams({ listMode: mode });
  if (templateId) {
    params.set("templateId", templateId);
  }
  const response = await axios.Get(`./emailSend?${params.toString()}`);
  return Array.isArray(response.data) ? response.data : [];
};

export const getEmailSendListPage = async (
  mode: EmailSendListMode,
  templateId: string | undefined,
  options: IEmailSendListQueryOptions & { pageIndex: number; pageSize: number },
): Promise<IEmailSendListPageResponse> => {
  const params = new URLSearchParams({ listMode: mode });
  if (templateId) {
    params.set("templateId", templateId);
  }
  params.set("pageIndex", String(options.pageIndex));
  params.set("pageSize", String(options.pageSize));
  if (options.sortField) {
    params.set("sortField", options.sortField);
  }
  if (options.sortOrder) {
    params.set("sortOrder", options.sortOrder);
  }
  const response = await axios.Get(`./emailSend?${params.toString()}`);
  const data = response.data;
  if (
    data &&
    typeof data === "object" &&
    Array.isArray(data.items) &&
    data.pagination
  ) {
    return data as IEmailSendListPageResponse;
  }
  const items = Array.isArray(data) ? data : [];
  return {
    items,
    pagination: {
      pageIndex: options.pageIndex,
      pageSize: options.pageSize,
      totalCount: items.length,
    },
  };
};

export const getEmailSendById = async (
  id: string,
): Promise<IEmailSend | null> => {
  const response = await axios.Get(`./emailSend?id=${encodeURIComponent(id)}`);
  return response?.data ?? null;
};

export interface IEmailSendRecipientRow {
  contact: string;
  email: string;
  firm: string;
  location: string;
  region: string;
}

export interface IEmailSendRecipientsResponse {
  listName: string;
  recipients: IEmailSendRecipientRow[];
  pagination?: IEmailSendListPagination;
}

export type IEmailSendRecipientsQueryOptions = {
  pageIndex: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
};

export const getEmailSendRecipients = async (
  emailSendId: string,
  options?: IEmailSendRecipientsQueryOptions,
): Promise<IEmailSendRecipientsResponse> => {
  const params = new URLSearchParams({
    recipients: "true",
    emailSendId,
  });
  if (options) {
    params.set("pageIndex", String(options.pageIndex));
    params.set("pageSize", String(options.pageSize));
    if (options.sortField) {
      params.set("sortField", options.sortField);
    }
    if (options.sortOrder) {
      params.set("sortOrder", options.sortOrder);
    }
  }
  const response = await axios.Get(`./emailSend?${params.toString()}`);
  return response.data;
};

export const cancelEmailSend = async (
  id: string,
): Promise<{ id: string; status: string }> => {
  const response = await axios.Delete("./emailSend", id);
  return response.data;
};

export const sendResetInvitation = async (userId: string) => {
  try {
    const response = await axios.Post(
      "/password?sendPasswordResetInvitation=true",
      { userId },
    );
    return response.data;
  } catch (error) {
    console.error("Error resetting password:", error);
    throw error;
  }
};
